import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { ConnectPayoutsList } from '@stripe/react-connect-js';
import {
  stripeAdminApi,
  type StripeDashboardMode,
  type PayoutReconciliationParams,
} from '../api/stripeAdminApi';

type ExportMode = 'payout' | 'period';
type PeriodPreset = 'this_month' | 'last_month' | 'this_quarter' | 'this_year';

const PERIOD_LABELS: Record<PeriodPreset, string> = {
  this_month: 'Ce mois-ci',
  last_month: 'Mois dernier',
  this_quarter: 'Ce trimestre',
  this_year: 'Cette année',
};

/** Bornes ISO [début, fin] d'un préset (fin = 23:59:59 du dernier jour). */
function computePeriod(preset: PeriodPreset): { start: string; end: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  let start: Date;
  let end: Date;
  switch (preset) {
    case 'last_month':
      start = new Date(y, m - 1, 1);
      end = new Date(y, m, 0, 23, 59, 59, 999);
      break;
    case 'this_quarter': {
      const q = Math.floor(m / 3) * 3;
      start = new Date(y, q, 1);
      end = new Date(y, q + 3, 0, 23, 59, 59, 999);
      break;
    }
    case 'this_year':
      start = new Date(y, 0, 1);
      end = new Date(y, 11, 31, 23, 59, 59, 999);
      break;
    case 'this_month':
    default:
      start = new Date(y, m, 1);
      end = new Date(y, m + 1, 0, 23, 59, 59, 999);
      break;
  }
  return { start: start.toISOString(), end: end.toISOString() };
}

const euro = (n: number, currency: string) =>
  `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency}`;

function payoutOptionLabel(p: {
  arrivalDate: number | null;
  amount: number;
  currency: string;
  status: string;
}): string {
  const date = p.arrivalDate
    ? new Date(p.arrivalDate * 1000).toLocaleDateString('fr-FR')
    : '—';
  return `${date} · ${euro(p.amount, p.currency)} · ${p.status}`;
}

const ExportModal: React.FC<{
  societyId: string;
  mode: StripeDashboardMode;
  onClose: () => void;
}> = ({ societyId, mode, onClose }) => {
  const [exportMode, setExportMode] = useState<ExportMode>('payout');
  const [payoutId, setPayoutId] = useState('');
  const [preset, setPreset] = useState<PeriodPreset>('this_month');
  const [busy, setBusy] = useState(false);

  const { data: payouts, isLoading } = useQuery({
    queryKey: ['stripe-admin', 'payouts', mode, societyId],
    queryFn: () => stripeAdminApi.getPayouts(societyId, mode),
    staleTime: 60_000,
  });

  const handleExport = async () => {
    let params: PayoutReconciliationParams;
    if (exportMode === 'payout') {
      if (!payoutId) {
        toast.error('Sélectionnez un payout à exporter.');
        return;
      }
      params = { payoutId };
    } else {
      const { start, end } = computePeriod(preset);
      params = { periodStart: start, periodEnd: end };
    }
    setBusy(true);
    try {
      await stripeAdminApi.exportPayoutReconciliation(societyId, params, mode);
      onClose();
    } catch (e: any) {
      toast.error(e?.message || 'Export impossible.');
    } finally {
      setBusy(false);
    }
  };

  const radioCls = (active: boolean) =>
    `tw-flex-1 tw-px-3 tw-py-2 tw-rounded-lg tw-border tw-text-sm tw-text-left tw-transition-colors ${
      active
        ? 'tw-border-affinicar-primary tw-bg-affinicar-primary/5 tw-text-affinicar-primary'
        : 'tw-border-gray-200 tw-text-gray-700 hover:tw-bg-gray-50'
    }`;
  const selectCls =
    'tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20';

  return createPortal(
    <div
      className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black/40 tw-p-4"
      onClick={onClose}
    >
      <div
        className="tw-w-full tw-max-w-md tw-bg-white tw-rounded-xl tw-shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-b tw-border-gray-200">
          <h3 className="tw-text-base tw-font-semibold tw-text-gray-900 tw-mb-0">
            Exporter le rapprochement
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="tw-text-gray-400 hover:tw-text-gray-600"
            aria-label="Fermer"
          >
            <X className="tw-w-5 tw-h-5" />
          </button>
        </div>

        <div className="tw-p-5 tw-space-y-4">
          <div className="tw-flex tw-gap-2">
            <button
              type="button"
              onClick={() => setExportMode('payout')}
              className={radioCls(exportMode === 'payout')}
            >
              Un payout
            </button>
            <button
              type="button"
              onClick={() => setExportMode('period')}
              className={radioCls(exportMode === 'period')}
            >
              Une période
            </button>
          </div>

          {exportMode === 'payout' ? (
            <div>
              <label className="tw-block tw-text-sm tw-text-gray-600 tw-mb-1">
                Payout
              </label>
              <select
                className={selectCls}
                value={payoutId}
                onChange={(e) => setPayoutId(e.target.value)}
                disabled={isLoading}
              >
                <option value="">
                  {isLoading ? 'Chargement…' : 'Sélectionner un payout'}
                </option>
                {(payouts ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {payoutOptionLabel(p)}
                  </option>
                ))}
              </select>
              {!isLoading && (payouts ?? []).length === 0 && (
                <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                  Aucun payout récent.
                </p>
              )}
            </div>
          ) : (
            <div>
              <label className="tw-block tw-text-sm tw-text-gray-600 tw-mb-1">
                Période
              </label>
              <select
                className={selectCls}
                value={preset}
                onChange={(e) => setPreset(e.target.value as PeriodPreset)}
              >
                {(Object.keys(PERIOD_LABELS) as PeriodPreset[]).map((k) => (
                  <option key={k} value={k}>
                    {PERIOD_LABELS[k]}
                  </option>
                ))}
              </select>
              <p className="tw-text-xs tw-text-gray-500 tw-mt-1">
                Tous les virements arrivés sur la période sélectionnée.
              </p>
            </div>
          )}
        </div>

        <div className="tw-flex tw-justify-end tw-gap-2 tw-px-5 tw-py-4 tw-border-t tw-border-gray-200">
          <button
            type="button"
            onClick={onClose}
            className="tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-lg tw-border tw-border-gray-200 tw-text-gray-700 hover:tw-bg-gray-50"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleExport}
            disabled={busy}
            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-lg tw-bg-affinicar-primary tw-text-white hover:tw-bg-affinicar-secondary disabled:tw-opacity-50"
          >
            {busy ? (
              <Loader2 className="tw-w-4 tw-h-4 tw-animate-spin" />
            ) : (
              <Download className="tw-w-4 tw-h-4" />
            )}
            Exporter
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

/**
 * Onglet « Rapprochement » : la liste des payouts (composant Stripe embarqué) +
 * un bouton d'export CSV du rapprochement comptable (composition exacte d'un
 * payout ou d'une période — paiements, frais, remboursements, litiges).
 */
export const PayoutReconciliationTab: React.FC<{
  societyId: string;
  mode?: StripeDashboardMode;
}> = ({ societyId, mode = 'self' }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="tw-space-y-4">
      <div className="tw-flex tw-justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-lg tw-bg-affinicar-primary tw-text-white hover:tw-bg-affinicar-secondary"
        >
          <Download className="tw-w-4 tw-h-4" />
          Exporter le rapprochement
        </button>
      </div>

      <ConnectPayoutsList />

      {open && (
        <ExportModal
          societyId={societyId}
          mode={mode}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};
